import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

interface CompensationStructure {
  id?: number
  structure_type: string
  base_rate: number
  currency: string
  effective_from: string
  effective_to?: string
  overtime_eligible: boolean
  overtime_rate_multiplier: number
  standard_hours_per_day: number
  standard_days_per_week: number
  notes?: string
}

interface Employee {
  id?: number
  employee_id: string  // Changed from employee_number to employee_id
  first_name: string
  last_name: string
  email: string
  phone: string
  department: string
  position: string
  hire_date: string
  status: string
  is_active?: boolean
  current_compensation_structure?: CompensationStructure
  pending_bonuses?: any[]
  effective_hourly_rate?: number
}

interface EmployeeFormData {
  employee_id: string  // Changed from employee_number to employee_id
  first_name: string
  last_name: string
  email: string
  phone: string
  department: string
  position: string
  hire_date: string
  status: string
  // Compensation structure fields
  compensation_structure_type: string
  base_rate: number
  currency: string
  effective_from: string
  overtime_eligible: boolean
  overtime_rate_multiplier: number
  standard_hours_per_day: number
  standard_days_per_week: number
  compensation_notes?: string
}

interface EmployeeDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: Partial<Employee>) => void
  employee?: Employee | null
  loading?: boolean
}

const schema = yup.object({
  employee_id: yup.string().required('Employee ID is required'),
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string().required('Phone is required'),
  department: yup.string().required('Department is required'),
  position: yup.string().required('Position is required'),
  hire_date: yup.string().required('Hire date is required'),
  status: yup.string().required('Status is required'),
  // Compensation structure validation
  compensation_structure_type: yup.string().required('Compensation structure type is required'),
  base_rate: yup.number().positive('Base rate must be positive').required('Base rate is required'),
  currency: yup.string().required('Currency is required'),
  effective_from: yup.string().required('Effective date is required'),
  overtime_eligible: yup.boolean().required(),
  overtime_rate_multiplier: yup.number().positive('Must be positive').required(),
  standard_hours_per_day: yup.number().positive('Must be positive').required(),
  standard_days_per_week: yup.number().positive('Must be positive').required(),
  compensation_notes: yup.string().optional(),
})

const COMPENSATION_TYPES = [
  { value: 'hourly_rate', label: 'Hourly Rate', description: 'Pay based on hours worked' },
  { value: 'daily_rate', label: 'Daily Rate', description: 'Fixed daily compensation' },
  { value: 'weekly_rate', label: 'Weekly Rate', description: 'Fixed weekly salary' },
  { value: 'monthly_salary', label: 'Monthly Salary', description: 'Fixed monthly compensation' },
]

// Currency is fixed to LKR - no selection needed
const CURRENCY = 'LKR'

export const EmployeeDialog: React.FC<EmployeeDialogProps> = ({
  open,
  onClose,
  onSubmit,
  employee,
  loading = false,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [compensationExpanded, setCompensationExpanded] = useState(true)
  
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<EmployeeFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      employee_id: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      department: '',
      position: '',
      hire_date: new Date().toISOString().split('T')[0],
      status: 'active',
      compensation_structure_type: 'hourly_rate',
      base_rate: 15.00,
      currency: CURRENCY,
      effective_from: new Date().toISOString().split('T')[0],
      overtime_eligible: true,
      overtime_rate_multiplier: 1.5,
      standard_hours_per_day: 8.0,
      standard_days_per_week: 5.0,
    },
  })

  const watchedCompensationType = watch('compensation_structure_type')

  useEffect(() => {
    if (employee) {
      const compensation = employee.current_compensation_structure
      
      // Log the compensation structure for debugging
      console.log('🔍 Loading employee for edit:', {
        employee_id: employee.employee_id,
        has_compensation: !!compensation,
        compensation_data: compensation
      })
      
      reset({
        employee_id: employee.employee_id,
        first_name: employee.first_name,
        last_name: employee.last_name,
        email: employee.email,
        phone: employee.phone,
        department: employee.department,
        position: employee.position,
        hire_date: employee.hire_date,
        status: employee.status,
        // Compensation structure fields - use actual values from backend, no fallbacks
        compensation_structure_type: compensation?.structure_type || 'hourly_rate',
        base_rate: compensation?.base_rate !== undefined ? Number(compensation.base_rate) : 15.00,
        currency: CURRENCY,
        effective_from: compensation?.effective_from || employee.hire_date,
        overtime_eligible: compensation?.overtime_eligible !== undefined ? compensation.overtime_eligible : true,
        overtime_rate_multiplier: compensation?.overtime_rate_multiplier !== undefined ? Number(compensation.overtime_rate_multiplier) : 1.5,
        standard_hours_per_day: compensation?.standard_hours_per_day !== undefined ? Number(compensation.standard_hours_per_day) : 8.0,
        standard_days_per_week: compensation?.standard_days_per_week !== undefined ? Number(compensation.standard_days_per_week) : 5.0,
        compensation_notes: compensation?.notes || '',
      })
      
      // Log what was set in the form
      console.log('✅ Form populated with values:', {
        base_rate: compensation?.base_rate !== undefined ? Number(compensation.base_rate) : 15.00,
        structure_type: compensation?.structure_type || 'hourly_rate'
      })
    } else {
      // Creating new employee - use defaults
      reset({
        employee_id: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        department: '',
        position: '',
        hire_date: new Date().toISOString().split('T')[0],
        status: 'active',
        compensation_structure_type: 'hourly_rate',
        base_rate: 15.00,
        currency: CURRENCY,
        effective_from: new Date().toISOString().split('T')[0],
        overtime_eligible: true,
        overtime_rate_multiplier: 1.5,
        standard_hours_per_day: 8.0,
        standard_days_per_week: 5.0,
        compensation_notes: '',
      })
    }
  }, [employee, reset])

  const handleFormSubmit = async (data: EmployeeFormData) => {
    const submitData: Partial<Employee> = {
      employee_id: data.employee_id,
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      phone: data.phone,
      department: data.department,
      position: data.position,
      hire_date: data.hire_date,
      status: data.status,
      is_active: data.status === 'active',  // Add is_active field for backend
    }

    // Add compensation structure data
    const compensationStructure: CompensationStructure = {
      structure_type: data.compensation_structure_type,
      base_rate: data.base_rate,
      currency: data.currency,
      effective_from: data.effective_from,
      overtime_eligible: data.overtime_eligible,
      overtime_rate_multiplier: data.overtime_rate_multiplier,
      standard_hours_per_day: data.standard_hours_per_day,
      standard_days_per_week: data.standard_days_per_week,
      notes: data.compensation_notes,
    }

    const finalSubmitData = {
      ...submitData,
      compensation_structure: compensationStructure,
    }

    if (employee && employee.id) {
      onSubmit({ ...finalSubmitData, id: employee.id })
    } else {
      onSubmit(finalSubmitData)
    }
  }

  const getCompensationTypeDescription = (type: string) => {
    const typeInfo = COMPENSATION_TYPES.find(t => t.value === type)
    return typeInfo?.description || ''
  }

  const getBaseRateLabel = (type: string) => {
    switch (type) {
      case 'hourly_rate': return 'Hourly Rate'
      case 'daily_rate': return 'Daily Rate'
      case 'weekly_rate': return 'Weekly Rate'
      case 'monthly_salary': return 'Monthly Salary'
      default: return 'Base Rate'
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: '20px' },
          backgroundColor: '#f5f7fa',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: '90vh' },
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        {employee ? 'Edit Employee' : 'Add New Employee'}
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: '#f5f7fa',
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
            '&:hover': {
              background: '#555',
            },
          },
          '& .MuiTextField-root': {
            mb: { xs: 2, sm: 3 },
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: '16px',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              '& fieldset': {
                borderColor: '#e0e0e0',
                borderWidth: '2px',
              },
              '&:hover fieldset': {
                borderColor: '#667eea',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#667eea',
                borderWidth: 2,
              },
              '&.Mui-error fieldset': {
                borderColor: 'error.main',
              },
            },
            '& .MuiOutlinedInput-input': {
              py: { xs: 1.5, sm: 2 },
            },
            '& .MuiInputLabel-root': {
              color: 'text.secondary',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              '&.Mui-focused': {
                color: '#667eea',
              },
              '&.Mui-error': {
                color: 'error.main',
              },
            },
            '& .MuiFormHelperText-root': {
              fontSize: '0.75rem',
              mt: 1,
            },
          },
          '& .MuiFormControl-root': {
            mb: { xs: 2, sm: 3 },
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: '16px',
              '& fieldset': {
                borderColor: '#e0e0e0',
                borderWidth: '2px',
              },
              '&:hover fieldset': {
                borderColor: '#667eea',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#667eea',
                borderWidth: 2,
              },
            },
            '& .MuiInputLabel-root': {
              color: 'text.secondary',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              '&.Mui-focused': {
                color: '#667eea',
              },
            },
          },
        }}
      >
        <Box component="form" sx={{ mt: { xs: 1, sm: 2 } }}>
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name="employee_id"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Employee ID"
                    fullWidth
                    error={!!errors.employee_id}
                    helperText={errors.employee_id?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.status}>
                    <InputLabel>Status</InputLabel>
                    <Select {...field} label="Status" disabled={loading}>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                      <MenuItem value="terminated">Terminated</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    fullWidth
                    error={!!errors.first_name}
                    helperText={errors.first_name?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name="last_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    fullWidth
                    error={!!errors.last_name}
                    helperText={errors.last_name?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone"
                    fullWidth
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name="department"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Department"
                    fullWidth
                    error={!!errors.department}
                    helperText={errors.department?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name="position"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Position"
                    fullWidth
                    error={!!errors.position}
                    helperText={errors.position?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Controller
                name="hire_date"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Hire Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.hire_date}
                    helperText={errors.hire_date?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            {/* Compensation Structure Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Accordion
                expanded={compensationExpanded}
                onChange={() => setCompensationExpanded(!compensationExpanded)}
                sx={{
                  backgroundColor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '8px 8px 0 0',
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      gap: 2,
                    },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Compensation Structure
                  </Typography>
                  <Chip
                    label="Required"
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                  <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                    <Grid item xs={12} sm={12} md={6}>
                      <Controller
                        name="compensation_structure_type"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors.compensation_structure_type}>
                            <InputLabel>Compensation Type</InputLabel>
                            <Select {...field} label="Compensation Type" disabled={loading}>
                              {COMPENSATION_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                  <Box>
                                    <Typography variant="body1">{type.label}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {type.description}
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.compensation_structure_type && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                {errors.compensation_structure_type.message}
                              </Typography>
                            )}
                          </FormControl>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                      {/* Currency is fixed to LKR */}
                      <TextField
                        label="Currency"
                        value="LKR (Rs.)"
                        disabled
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                      <Controller
                        name="base_rate"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={getBaseRateLabel(watchedCompensationType)}
                            type="number"
                            fullWidth
                            error={!!errors.base_rate}
                            helperText={errors.base_rate?.message || getCompensationTypeDescription(watchedCompensationType)}
                            disabled={loading}
                            inputProps={{ step: 0.01, min: 0 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                      <Controller
                        name="effective_from"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Effective From"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.effective_from}
                            helperText={errors.effective_from?.message}
                            disabled={loading}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                      <Controller
                        name="overtime_rate_multiplier"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Overtime Rate Multiplier"
                            type="number"
                            fullWidth
                            error={!!errors.overtime_rate_multiplier}
                            helperText={errors.overtime_rate_multiplier?.message || "e.g., 1.5 for time-and-a-half"}
                            disabled={loading}
                            inputProps={{ step: 0.1, min: 1 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                      <Controller
                        name="overtime_eligible"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={field.value}
                                onChange={field.onChange}
                                disabled={loading}
                              />
                            }
                            label="Eligible for Overtime"
                            sx={{ mt: 2 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                      <Controller
                        name="standard_hours_per_day"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Standard Hours per Day"
                            type="number"
                            fullWidth
                            error={!!errors.standard_hours_per_day}
                            helperText={errors.standard_hours_per_day?.message}
                            disabled={loading}
                            inputProps={{ step: 0.5, min: 1, max: 24 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                      <Controller
                        name="standard_days_per_week"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Standard Days per Week"
                            type="number"
                            fullWidth
                            error={!!errors.standard_days_per_week}
                            helperText={errors.standard_days_per_week?.message}
                            disabled={loading}
                            inputProps={{ step: 0.5, min: 1, max: 7 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="compensation_notes"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Compensation Notes"
                            multiline
                            rows={3}
                            fullWidth
                            error={!!errors.compensation_notes}
                            helperText={errors.compensation_notes?.message || "Optional notes about the compensation structure"}
                            disabled={loading}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: 'white',
          borderTop: '2px solid #f0f0f0',
          p: { xs: 2, sm: 3 },
          gap: { xs: 1, sm: 2 },
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: '16px',
            borderColor: '#e0e0e0',
            borderWidth: '2px',
            color: 'text.primary',
            fontWeight: 600,
            px: { xs: 3, sm: 4 },
            py: { xs: 1.25, sm: 1.5 },
            textTransform: 'none',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            '&:hover': {
              backgroundColor: '#f5f5f5',
              borderColor: '#bdbdbd',
              borderWidth: '2px',
            },
            '&:disabled': {
              opacity: 0.6,
              borderColor: '#e0e0e0',
              color: 'text.secondary',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(handleFormSubmit as any)}
          variant="contained"
          disabled={loading}
          fullWidth
          sx={{
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontWeight: 600,
            px: { xs: 3, sm: 4 },
            py: { xs: 1.25, sm: 1.5 },
            textTransform: 'none',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3d8f 100%)',
              boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
            },
            '&:active': {
              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
            },
            '&:disabled': {
              opacity: 0.6,
              background: '#e0e0e0',
              color: 'text.secondary',
              boxShadow: 'none',
            },
          }}
        >
          {loading ? 'Saving...' : employee ? 'Update Employee' : 'Create Employee'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EmployeeDialog
